import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"
import { expandedIdFromHash, writeExpandedFragment, migrateLegacyExpandParam } from "../support/expand_fragment"

/**
 * Inline Create Controller
 *
 * Creates new resources inline without opening a modal. Supports factory mode
 * for rapid-fire creation (type, enter, type, enter...).
 *
 * Requires: element must have an `id` attribute for URL-based state persistence.
 *
 * Usage (click-to-reveal):
 *   <div id="inline-create-slots"
 *        data-controller="inline-create"
 *        data-inline-create-url-value="/account/plans/123/slots"
 *        data-inline-create-model-value="staffing_plans_slot"
 *        data-inline-create-fallback-url-value="/account/plans/123/slots/new">
 *     <a href="#" data-inline-create-target="trigger"
 *        data-action="click->inline-create#showInput">+ Add slot</a>
 *     <div data-inline-create-target="inputWrapper" class="hidden">
 *       <input data-inline-create-target="input"
 *              data-action="keydown.enter->inline-create#create keydown.esc->inline-create#cancel blur->inline-create#handleBlur" />
 *     </div>
 *   </div>
 */
export default class extends Controller {
  static targets = ["input", "trigger", "inputWrapper"]

  static values = {
    url: String,
    model: String,
    field: { type: String, default: "name" },
    extraParams: { type: Object, default: {} },
    fallbackUrl: String,
    alwaysExpanded: { type: Boolean, default: false },
    clearExpandedOnSubmit: { type: Boolean, default: false },
    creating: { type: Boolean, default: false }
  }

  connect() {
    this.handleTurboRender = this.handleTurboRender?.bind(this) || this.onTurboRender.bind(this)
    document.addEventListener("turbo:render", this.handleTurboRender)
    document.addEventListener("turbo:load", this.handleTurboRender)
    this.migrateLegacyExpandParam()
    this.observeVisibility()
    this.persistExpandedUrlIfVisible()
    this.restoreExpandedState()
  }

  disconnect() {
    document.removeEventListener("turbo:render", this.handleTurboRender)
    document.removeEventListener("turbo:load", this.handleTurboRender)
    this.visibilityObserver?.disconnect()
  }

  inputTargetConnected() {
    this.observeVisibility()
    this.persistExpandedUrlIfVisible()
    this.restoreExpandedState()
  }

  inputWrapperTargetConnected() {
    this.observeVisibility()
    this.persistExpandedUrlIfVisible()
    this.restoreExpandedState()
  }

  triggerTargetConnected() {
    this.observeVisibility()
    this.persistExpandedUrlIfVisible()
    this.restoreExpandedState()
  }

  // --- URL-based state for factory mode ---
  // Fragment-backed; the mechanics and the reasoning live in
  // support/expand_fragment.js, shared with inline_assigned_job_create.

  expandedIdFromHash() {
    return expandedIdFromHash()
  }

  shouldAutoExpand() {
    if (!this.element.id || this.alwaysExpandedValue) return false

    return expandedIdFromHash() === this.element.id
  }

  setExpandedUrl(active) {
    writeExpandedFragment(this.element.id, active && !this.alwaysExpandedValue)
  }

  migrateLegacyExpandParam() {
    migrateLegacyExpandParam(this.element.id)
  }

  // --- Show/Hide ---

  restoreExpandedState() {
    // Morphing can replace targets without reconnecting the controller root.
    const alwaysExpanded = this.alwaysExpandedValue

    if (alwaysExpanded) {
      this.renderExpandedState()
      this.focusInput()
    } else if (this.shouldAutoExpand()) {
      this.renderExpandedState()
      this.focusInput()
    } else {
      this.renderCollapsedState()
    }
  }

  renderExpandedState() {
    if (this.hasTriggerTarget) {
      this.triggerTarget.classList.add("hidden")
    }

    if (this.hasInputWrapperTarget) {
      this.inputWrapperTarget.classList.remove("hidden")
    }
  }

  renderCollapsedState() {
    if (this.hasInputWrapperTarget) {
      this.inputWrapperTarget.classList.add("hidden")
    }

    if (this.hasInputTarget) {
      this.inputTarget.value = ""
    }

    if (this.hasTriggerTarget) {
      this.triggerTarget.classList.remove("hidden")
    }
  }

  expandedStateDrifted() {
    if (!this.hasInputWrapperTarget) return false

    const shouldBeExpanded = this.alwaysExpandedValue || this.shouldAutoExpand()
    const wrapperHidden = this.inputWrapperTarget.classList.contains("hidden")
    const triggerVisible = this.hasTriggerTarget && !this.triggerTarget.classList.contains("hidden")
    const triggerDrifted = this.hasTriggerTarget && (shouldBeExpanded ? triggerVisible : !triggerVisible)
    return (shouldBeExpanded ? wrapperHidden : !wrapperHidden) || triggerDrifted
  }

  scheduleRestore() {
    if (this.restoreScheduled) return
    this.restoreScheduled = true

    setTimeout(() => {
      this.restoreScheduled = false
      this.restoreExpandedState()
    }, 0)
  }

  showInput(event) {
    event?.preventDefault()
    this.setExpandedUrl(true)
    this.renderExpandedState()
    this.focusInput()
  }

  hideInput() {
    if (this.hasInputWrapperTarget) {
      this.inputWrapperTarget.classList.add("hidden")
    }

    if (this.hasInputTarget) {
      this.inputTarget.value = ""
    }

    if (this.hasTriggerTarget) {
      this.triggerTarget.classList.remove("hidden")
    }
  }

  focusInput() {
    if (!this.hasInputTarget) return

    // Multiple attempts - Turbo may steal focus during DOM updates
    ;[0, 50, 150].forEach(delay => {
      setTimeout(() => {
        if (this.hasInputTarget && this.canRestoreInputFocus()) {
          this.inputTarget.focus()
        }
      }, delay)
    })
  }

  canRestoreInputFocus() {
    const activeElement = document.activeElement
    if (!activeElement || activeElement === document.body || activeElement === this.inputTarget) return true

    return this.element.contains(activeElement)
  }

  persistExpandedUrlIfVisible() {
    if (this.alwaysExpandedValue) {
      return
    }

    if (!this.isExpanded()) return
    const currentExpandedId = this.expandedIdFromHash()
    if (currentExpandedId && currentExpandedId !== this.element.id) return
    if (!this.shouldAutoExpand()) {
      this.renderCollapsedState()
      return
    }
    this.setExpandedUrl(true)
  }

  isExpanded() {
    if (!this.hasInputWrapperTarget) return false
    return !this.inputWrapperTarget.classList.contains("hidden")
  }

  // --- Actions ---

  handleBlur(event) {
    // Don't hide if clicking inside this controller (e.g., "More options" link)
    if (event.relatedTarget && this.element.contains(event.relatedTarget)) {
      return
    }

    // Delay to allow click events to fire first
    setTimeout(() => {
      if (this.element.contains(document.activeElement)) return

      const value = this.inputTarget?.value.trim()
      if (value) {
        this.create()
      } else {
        // Empty blur always closes the editor; empty create (if supported) requires explicit Enter
        this.cancel()
      }
    }, 150)
  }

  cancel() {
    this.setExpandedUrl(false)
    this.hideInput()
    this.dispatch("cancelled")
  }

  // Called when user clicks "More options" to open modal instead
  exitToModal(event) {
    event?.preventDefault()

    const url = new URL(event.currentTarget.href)
    const value = this.inputTarget?.value.trim()
    if (value) {
      url.searchParams.set(`${this.modelValue}[${this.fieldValue}]`, value)
    }

    this.cancel()
    url.searchParams.set("return_to", window.location.href)

    const link = document.createElement("a")
    link.href = url.toString()
    link.dataset.turboFrame = "modal"
    document.body.appendChild(link)
    link.click()
    link.remove()
  }

  async create(event) {
    event?.preventDefault()
    if (this.creatingValue) return

    const value = this.inputTarget?.value.trim() ?? ""
    // Empty is only reachable via explicit confirm (Enter); implicit exit (blur) cancels instead.
    // When model allows optional field (e.g. slot name), POST empty so backend can create unnamed record.

    this.creatingValue = true
    this.element.classList.add("opacity-50")
    document.dispatchEvent(new CustomEvent("canvas-activity:start", {
      bubbles: true,
      detail: { control: document.activeElement }
    }))

    // Inline refreshes resolve against the live client URL when the response lands.
    // Even the always-expanded empty state needs this on create so the first
    // refresh into the non-empty canvas keeps factory mode open.
    if (this.alwaysExpandedValue && this.element.id) {
      // setExpandedUrl refuses to write for an always-expanded control, and
      // this is the one moment it has to: the refresh lands on the non-empty
      // canvas, where this control is an ordinary collapsible one again, and
      // factory mode has to survive the change.
      writeExpandedFragment(this.element.id, true)
    } else if (this.clearExpandedOnSubmitValue) {
      this.setExpandedUrl(false)
    } else {
      this.setExpandedUrl(true)
    }

    try {
      const body = this.buildRequestBody(value)
      document.dispatchEvent(new CustomEvent("canvas-activity:init", { bubbles: true }))
      const response = await post(this.urlValue, {
        body: JSON.stringify(body),
        contentType: "application/json",
        responseKind: "turbo-stream"
      })

      if (response.ok) {
        this.dispatch("created", { detail: { value } })
        if (this.hasInputTarget) {
          this.inputTarget.value = ""
          this.inputTarget.focus()
        }
      } else {
        this.setExpandedUrl(false) // Revert on error
        this.escalateToModal(value)
      }
    } catch (error) {
      console.error("Inline create error:", error)
      this.setExpandedUrl(false) // Revert on error
      this.escalateToModal(value)
    } finally {
      this.creatingValue = false
      this.element.classList.remove("opacity-50")
      document.dispatchEvent(new CustomEvent("canvas-activity:complete", { bubbles: true }))
      document.dispatchEvent(new CustomEvent("canvas-activity:visible", { bubbles: true }))
    }
  }

  buildRequestBody(value) {
    const params = { ...this.extraParamsValue }
    // Always send the field so explicit empty confirm can create unnamed record when model allows
    params[this.fieldValue] = value ?? ""
    const body = this.modelValue ? { [this.modelValue]: params } : params
    body.refresh_mode = "current"
    return body
  }

  escalateToModal(value) {
    if (!this.fallbackUrlValue) {
      this.showError()
      return
    }

    const url = new URL(this.fallbackUrlValue, window.location.origin)
    if (value) {
      url.searchParams.set(`${this.modelValue}[${this.fieldValue}]`, value)
    }
    url.searchParams.set("return_to", window.location.href)

    const link = document.createElement("a")
    link.href = url.toString()
    link.dataset.turboFrame = "modal"
    document.body.appendChild(link)
    link.click()
    link.remove()

    this.inputTarget && (this.inputTarget.value = "")
  }

  showError() {
    this.element.classList.add("ring-2", "ring-red-500")
    setTimeout(() => this.element.classList.remove("ring-2", "ring-red-500"), 2000)
  }

  onTurboRender() {
    // A later Turbo visit can arrive carrying ?expand= from a server-rendered
    // return_to; connect() will not run again for a permanent element, so the
    // migration has to happen here as well.
    this.migrateLegacyExpandParam()
    if (!this.expandedStateDrifted()) return

    ;[0, 25, 100, 250].forEach(delay => {
      setTimeout(() => this.restoreExpandedState(), delay)
    })
  }

  observeVisibility() {
    this.visibilityObserver?.disconnect()
    this.visibilityObserver = new MutationObserver(mutations => {
      mutations.forEach(mutation => {
        if (mutation.type !== "attributes" || mutation.attributeName !== "class") return

        if (this.expandedStateDrifted()) {
          this.scheduleRestore()
        }
      })
    })

    if (this.hasTriggerTarget) {
      this.visibilityObserver.observe(this.triggerTarget, {attributes: true, attributeFilter: ["class"]})
    }

    if (this.hasInputWrapperTarget) {
      this.visibilityObserver.observe(this.inputWrapperTarget, {attributes: true, attributeFilter: ["class"]})
    }
  }
}
